/**
 * EditDisplayNameSheet Component
 *
 * A bottom sheet modal for editing the user's display name.
 * Shows character count and validates input before saving.
 *
 * @example
 * ```tsx
 * <EditDisplayNameSheet
 *   isOpen={showEditSheet}
 *   currentName="John Doe"
 *   onClose={() => setShowEditSheet(false)}
 *   onSave={async (name) => await updateDisplayName(name)}
 * />
 * ```
 */

import { useState, type CSSProperties } from 'react';

interface EditDisplayNameSheetProps {
  /** Whether the sheet is visible */
  isOpen: boolean;
  /** The current display name to pre-fill the input */
  currentName: string;
  /** Callback when the sheet is closed */
  onClose: () => void;
  /** Callback when save is clicked with the new name */
  onSave: (name: string) => Promise<void>;
}

const MAX_LENGTH = 30;

/**
 * EditDisplayNameSheet displays a bottom sheet modal for editing display name
 */
export function EditDisplayNameSheet({
  isOpen,
  currentName,
  onClose,
  onSave,
}: EditDisplayNameSheetProps) {
  const [name, setName] = useState(currentName);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!name.trim() || saving) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(name.trim());
      onClose();
    } catch {
      setError('Failed to update display name. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length <= MAX_LENGTH) {
      setName(value);
    }
  };

  return (
    <>
      <div style={overlayStyle} onClick={onClose} />
      <div style={sheetStyle}>
        <h2 style={titleStyle}>Edit Display Name</h2>
        <p style={subtitleStyle}>This is how other climbers will see you.</p>
        <input type="text" value={name} onChange={handleChange} style={inputStyle} autoFocus />
        <span style={charCountStyle}>
          {name.length}/{MAX_LENGTH} characters
        </span>
        {error && <p style={errorStyle}>{error}</p>}
        <div style={buttonsStyle}>
          <button style={cancelButtonStyle} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button style={saveButtonStyle} onClick={handleSave} disabled={!name.trim() || saving}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </>
  );
}

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  zIndex: 999,
};

const sheetStyle: CSSProperties = {
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: 'var(--color-bg-primary)',
  borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0',
  padding: 'var(--spacing-lg)',
  zIndex: 1000,
};

const titleStyle: CSSProperties = {
  fontSize: 'var(--font-size-lg)',
  fontWeight: 600,
  margin: 0,
  marginBottom: 'var(--spacing-xs)',
};

const subtitleStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-secondary)',
  margin: 0,
  marginBottom: 'var(--spacing-md)',
};

const inputStyle: CSSProperties = {
  width: '100%',
  padding: 'var(--spacing-sm)',
  fontSize: 'var(--font-size-base)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  boxSizing: 'border-box',
};

const charCountStyle: CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-secondary)',
  marginTop: 'var(--spacing-xs)',
  marginBottom: 'var(--spacing-md)',
};

const buttonsStyle: CSSProperties = {
  display: 'flex',
  gap: 'var(--spacing-sm)',
  justifyContent: 'flex-end',
};

const cancelButtonStyle: CSSProperties = {
  padding: 'var(--spacing-sm) var(--spacing-md)',
  fontSize: 'var(--font-size-base)',
  backgroundColor: 'transparent',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  cursor: 'pointer',
};

const errorStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-error, #dc2626)',
  margin: 0,
  marginBottom: 'var(--spacing-sm)',
};

const saveButtonStyle: CSSProperties = {
  padding: 'var(--spacing-sm) var(--spacing-md)',
  fontSize: 'var(--font-size-base)',
  backgroundColor: 'var(--color-primary)',
  color: 'white',
  border: 'none',
  borderRadius: 'var(--radius-md)',
  cursor: 'pointer',
};

export default EditDisplayNameSheet;
